// Purchase Management API Service Functions
// Provides API integration for suppliers, purchase orders, and analytics

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

// Suppliers API
export const supplierService = {
  // Get all suppliers with optional filters
  async getAll(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.active !== undefined) queryParams.append('active', params.active);
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetch(`${API_BASE_URL}/suppliers?${queryParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch suppliers');
    }
    return response.json();
  },

  // Get single supplier by ID
  async getById(id) {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch supplier');
    }
    return response.json();
  },

  // Create new supplier
  async create(supplierData) {
    const response = await fetch(`${API_BASE_URL}/suppliers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(supplierData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create supplier');
    }
    return response.json();
  },

  // Update supplier
  async update(id, supplierData) {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(supplierData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update supplier');
    }
    return response.json();
  },

  // Delete (deactivate) supplier
  async delete(id) {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete supplier');
    }
    return response.json();
  },

  // Get supplier performance metrics
  async getPerformance(id) {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}/performance`);
    if (!response.ok) {
      throw new Error('Failed to fetch supplier performance');
    }
    return response.json();
  }
};

// Purchase Orders API
export const purchaseService = {
  // Get all purchase orders with optional filters
  async getAll(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.supplierId) queryParams.append('supplierId', params.supplierId);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetch(`${API_BASE_URL}/purchases?${queryParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch purchase orders');
    }
    return response.json();
  },

  // Get single purchase order by ID
  async getById(id) {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch purchase order');
    }
    return response.json();
  },

  // Create new purchase order
  async create(purchaseOrderData) {
    const response = await fetch(`${API_BASE_URL}/purchases`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(purchaseOrderData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create purchase order');
    }
    return response.json();
  },

  // Update purchase order
  async update(id, purchaseOrderData) {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(purchaseOrderData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update purchase order');
    }
    return response.json();
  },

  // Update purchase order status
  async updateStatus(id, updateData) {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update purchase status');
    }
    return response.json();
  },

  // Update delivery date
  async updateDeliveryDate(id, deliveryDate) {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}/delivery-date`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ deliveryDate }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update delivery date');
    }
    return response.json();
  },

  // Receive purchase order (mark items as received)
  async receive(id, receivingData) {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}/receive`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(receivingData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to receive purchase order');
    }
    return response.json();
  },

  // Cancel purchase order
  async cancelPurchaseOrder(id, reason = '') {
    const response = await fetch(`${API_BASE_URL}/api/purchases/${id}/cancel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to cancel purchase order');
    }
    return response.json();
  },

  // Update payment status
  async updatePaymentStatus(id, paymentData) {
    const response = await fetch(`${API_BASE_URL}/api/purchases/${id}/payment`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update payment status');
    }
    return response.json();
  },

  // Download purchase history as Excel file
  async downloadHistory(type, selectedDate = null, supplierId = null) {
    let endpoint;
    const queryParams = new URLSearchParams();
    
    // Add supplier filter if provided
    if (supplierId && supplierId !== '') {
      queryParams.append('supplierId', supplierId);
    }
    
    switch (type) {
      case 'day':
        if (!selectedDate) {
          throw new Error('Date is required for daily export');
        }
        endpoint = `${API_BASE_URL}/purchases/export/day/${selectedDate}`;
        break;
      case 'week':
        endpoint = `${API_BASE_URL}/purchases/export/week`;
        break;
      case 'month':
        endpoint = `${API_BASE_URL}/purchases/export/month`;
        break;
      case 'threemonths':
        endpoint = `${API_BASE_URL}/purchases/export/threemonths`;
        break;
      default:
        throw new Error('Invalid export type');
    }

    // Add query parameters if any
    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await fetch(endpoint, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to download file' }));
      throw new Error(error.message || 'Failed to download purchase history');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'Compras_export.xlsx';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Convert response to blob
    const blob = await response.blob();
    
    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return { success: true, filename };
  }
};

// Purchase Analytics API
export const purchaseAnalyticsService = {
  // Get purchase overview analytics
  async getOverview(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.period) queryParams.append('period', params.period);

    const response = await fetch(`${API_BASE_URL}/purchase-analytics/overview?${queryParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch purchase overview');
    }
    return response.json();
  },

  // Get supplier performance analytics
  async getSupplierPerformance() {
    const response = await fetch(`${API_BASE_URL}/purchase-analytics/supplier-performance`);
    if (!response.ok) {
      throw new Error('Failed to fetch supplier performance');
    }
    return response.json();
  },

  // Get cost trends analytics
  async getCostTrends(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.period) queryParams.append('period', params.period);

    const response = await fetch(`${API_BASE_URL}/purchase-analytics/cost-trends?${queryParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch cost trends');
    }
    return response.json();
  },

  // Get cash flow analytics
  async getCashFlow() {
    const response = await fetch(`${API_BASE_URL}/purchase-analytics/cash-flow`);
    if (!response.ok) {
      throw new Error('Failed to fetch cash flow analysis');
    }
    return response.json();
  }
};

// Helper functions
export const purchaseHelpers = {
  // Format currency (amounts are in pesos)
  formatCurrency: (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return `$${Math.round(amount)}`;
  },

  // Format date for display
  formatDate: (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  },

  // Get status color for purchase orders
  getStatusColor: (status) => {
    switch (status) {
      case 'received': return '#27ae60'; // green
      case 'partial': return '#f39c12'; // orange
      case 'pending': return '#3498db'; // blue
      case 'cancelled': return '#e74c3c'; // red
      default: return '#95a5a6'; // gray
    }
  },

  // Get payment status color
  getPaymentStatusColor: (status) => {
    switch (status) {
      case 'paid': return '#27ae60'; // green
      case 'pending': return '#f39c12'; // orange
      case 'overdue': return '#e74c3c'; // red
      default: return '#95a5a6'; // gray
    }
  },

  // Format purchase order items summary
  formatItemsSummary: (items) => {
    if (!items || items.length === 0) return 'Sin productos';
    
    if (items.length <= 2) {
      return items.map(item => 
        `${item.productName} x${item.quantityOrdered}`
      ).join(', ');
    } else {
      const first = items[0];
      return `${first.productName} x${first.quantityOrdered} y ${items.length - 1} más`;
    }
  },

  // Get available dates for purchase orders (similar to orders)
  async getAvailablePurchaseDates() {
    try {
      const response = await purchaseService.getPurchaseOrders({ limit: 1000 });
      const purchases = response.data || [];
      
      // Extract unique dates
      const dates = [...new Set(
        purchases.map(purchase => purchase.orderDate.split('T')[0])
      )].sort((a, b) => new Date(b) - new Date(a)); // Most recent first
      
      return dates;
    } catch (error) {
      console.error('Error getting available purchase dates:', error);
      return [];
    }
  }
};

// Export all services as default
const purchaseApiService = {
  suppliers: supplierService,
  purchases: purchaseService,
  analytics: purchaseAnalyticsService,
  helpers: purchaseHelpers
};

export default purchaseApiService;